import { useCallback, useState } from 'react';
import { useMap } from '@lib/useMap';
import { useCreateSignedUrlMutation } from '@views/ProofDocuments/components/DocumentSection/graphql/useCreateDocumentSignedUrl';
import { useUploadDocument } from './useUploadDocument';
import { fileValidator } from '../fileValidator';

export const useFileUploaderOptions = ({
  control,
  documentOptions,
  onUploadError = () => {},
  clearField: clearFieldProp,
  onUploadCompleted = () => {},
}) => {
  const [createDocumentSignedUrl] = useCreateSignedUrlMutation();
  const [uploadingFieldName, setUploadingFieldName] = useState();
  const {
    map: filesWithPassword,
    setValue: setFileWithPassword,
  } = useMap();
  const {
    progress,
    uploadDocument,
    isLoading: isUploading,
  } = useUploadDocument();

  const isLoading = isUploading || !!uploadingFieldName;

  const clearField = useCallback(({ fieldName }) => {
    clearFieldProp({ fieldName });
    setFileWithPassword(fieldName, null);
  }, [clearFieldProp, setFileWithPassword]);

  const onUploadDocument = useCallback(async ({
    file,
    fieldName,
    password = null,
  }) => {
    setUploadingFieldName(fieldName);
    await createDocumentSignedUrl({
      variables: {
        input: {
          documentClass: documentOptions.documentClass,
          documentType: documentOptions.documentType,
          documentGrouping: documentOptions.documentGrouping,
          passwordDocument: password,
          fileName: file.name,
        },
      },
      onCompleted: async (data) => {
        const { signedUrl } = data.createDocumentSignedUrl;
        await uploadDocument({ file, signedUrl });
        setUploadingFieldName(null);
        onUploadCompleted({ fieldName });
        setFileWithPassword(fieldName, null);
      },
      onError: (error) => {
        setUploadingFieldName(null);
        onUploadError({ fieldName, error });
      },
    });
  }, [
    onUploadError,
    uploadDocument,
    onUploadCompleted,
    setFileWithPassword,
    createDocumentSignedUrl,
    documentOptions.documentClass,
    documentOptions.documentType,
    documentOptions.documentGrouping,
  ]);

  const onSubmitFile = useCallback(async (fieldName, file) => {
    const error = await fileValidator({ file });
    if (!error?.errorCode) setFileWithPassword(fieldName, false);
    if (!error) onUploadDocument({ file, fieldName });
    else if (error.errorCode === 'NEED_PASSWORD') setFileWithPassword(fieldName, true);
  }, [
    onUploadDocument,
    setFileWithPassword,
  ]);

  const onSubmitWithPassword = useCallback(async ({
    file,
    password,
    fieldName,
  }) => {
    const error = await fileValidator({ file, password });
    if (!error?.errorCode) setFileWithPassword(fieldName, false);
    if (!error) onUploadDocument({ file, fieldName, password });
    else if (['INVALID_PASSWORD', 'NEED_PASSWORD']
      .includes(error.errorCode)) setFileWithPassword(fieldName, true);
  }, [
    onUploadDocument,
    setFileWithPassword,
  ]);

  return {
    control,
    progress,
    clearField,
    onSubmitFile,
    filesWithPassword,
    uploadingFieldName,
    onSubmitWithPassword,
    isUploading: isLoading,
  };
};
